import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Alert,
  Skeleton,
  Stack,
  Fade,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LinkIcon from '@mui/icons-material/Link';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { collection, getDocs, doc, getDoc, onSnapshot, setDoc, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../../../../shared/utils/firebase';
import { useAuth } from '../../../auth/useAuth';
import { useSnackbar } from '../../../../shared/utils/snackbar';
import { AssessmentStatusHeader } from './AssessmentStatusHeader';
import { PreAssessmentCards } from './PreAssessmentCards';
import { ProgrammeAssessmentSection } from './ProgrammeAssessmentSection';
import { ReportGenerationModal } from './ReportGenerationModal';
import { ProgrammeSelectionModal } from './ProgrammeSelectionModal';
import { AssessmentSectionProps, AssessmentData, PreAssessmentType, AssessmentCategory } from './types';
import { AlliedHealthAssessmentDialog } from './AlliedHealthAssessmentDialog';
import ClientAssessmentViewer from '../../ClientAssessmentViewer';

// Helper function to extract file ID from Google Drive URL
const extractFileIdFromUrl = (url: string): string => {
  // Handle various Google Drive URL formats
  if (!url) return '';
  
  // Format: https://drive.google.com/file/d/FILE_ID/view
  const filePathMatch = url.match(/\/file\/d\/([^/]+)/);
  if (filePathMatch && filePathMatch[1]) return filePathMatch[1];
  
  // Format: https://drive.google.com/open?id=FILE_ID
  const openIdMatch = url.match(/[?&]id=([^&]+)/);
  if (openIdMatch && openIdMatch[1]) return openIdMatch[1];
  
  // Format: https://drive.google.com/uc?id=FILE_ID
  const ucIdMatch = url.match(/[?&]id=([^&]+)/);
  if (ucIdMatch && ucIdMatch[1]) return ucIdMatch[1];
  
  // Format: https://docs.google.com/document/d/FILE_ID/edit
  const docsMatch = url.match(/\/d\/([^/]+)/);
  if (docsMatch && docsMatch[1]) return docsMatch[1];
  
  // If no match found, return empty string
  return '';
};

type WellbeingValue = string | number | null | undefined;

interface WellbeingScores {
  healthRating: WellbeingValue;
  moderateExerciseMinutes: WellbeingValue;
  resistanceTrainingSessions: WellbeingValue;
  vigorousExerciseMinutes: WellbeingValue;
  wellbeingIndexScore: WellbeingValue;
  wellbeingIndexResponses?: string[];
  rawWellbeingIndex?: string;
}

const EMPTY_WELLBEING_SCORES: WellbeingScores = {
  healthRating: null,
  moderateExerciseMinutes: null,
  resistanceTrainingSessions: null,
  vigorousExerciseMinutes: null,
  wellbeingIndexScore: null,
};

const getSubmissionChipSx = (stage: 'Initial' | 'Review', submitted: boolean) => (theme: Theme) => {
  const submittedColor = stage === 'Review' ? theme.palette.info.main : theme.palette.success.main;
  const backgroundColor = alpha(submitted ? submittedColor : theme.palette.warning.main, submitted ? 0.12 : 0.14);
  const foregroundColor = submitted ? submittedColor : theme.palette.warning.dark;

  return {
    backgroundColor,
    color: foregroundColor,
    fontWeight: 600,
    borderRadius: '999px',
    px: 1,
    '& .MuiChip-icon': {
      color: 'inherit',
      ml: '-4px',
      mr: '-6px',
      fontSize: '1rem',
    },
  };
};

type WellbeingMetricKey =
  | 'healthRating'
  | 'moderateExerciseMinutes'
  | 'resistanceTrainingSessions'
  | 'vigorousExerciseMinutes'
  | 'wellbeingIndexScore';

const WELLBEING_METRICS: Array<{ key: WellbeingMetricKey; label: string }> = [
  { key: 'healthRating', label: 'Health Rating' },
  { key: 'moderateExerciseMinutes', label: 'Moderate Exercise Minutes Per Week' },
  { key: 'resistanceTrainingSessions', label: 'Resistance Training Sessions Per Week' },
  { key: 'vigorousExerciseMinutes', label: 'Vigorous Exercise Minutes Per Week' },
  { key: 'wellbeingIndexScore', label: 'Wellbeing Index (0-25)' },
];

const getWellbeingIcon = (key: WellbeingMetricKey) => {
  switch (key) {
    case 'healthRating':
      return <FavoriteBorderIcon fontSize="small" />;
    case 'moderateExerciseMinutes':
      return <DirectionsWalkIcon fontSize="small" />;
    case 'resistanceTrainingSessions':
      return <FitnessCenterIcon fontSize="small" />;
    case 'vigorousExerciseMinutes':
      return <FlashOnIcon fontSize="small" />;
    case 'wellbeingIndexScore':
    default:
      return <EmojiEmotionsIcon fontSize="small" />;
  }
};

const WELLBEING_INDEX_SCORE_MAP: Record<string, number> = {
  'At no time': 0,
  'Some of the time': 1,
  'Less than half of the time': 2,
  'More than half of the time': 3,
  'Most of the time': 4,
  'All of the time': 5,
};

const parseWellbeingIndex = (value: string | null | undefined) => {
  const responses = value
    ? value
        .split(',')
        .map(response => response.trim())
        .filter(Boolean)
    : [];

  if (!responses.length) {
    return { responses, score: null };
  }

  const score = responses.reduce((total, response) => {
    return total + (WELLBEING_INDEX_SCORE_MAP[response] ?? 0);
  }, 0);

  return { responses, score };
};

const isWellbeingValueMissing = (value: WellbeingValue) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
};

const getWellbeingDisplay = (value: WellbeingValue) => {
  if (isWellbeingValueMissing(value)) {
    return { label: 'Awaiting Submission', pending: true };
  }

  if (typeof value === 'number') {
    return { label: Number.isFinite(value) ? value.toString() : 'Awaiting Submission', pending: !Number.isFinite(value) };
  }

  if (typeof value === 'string') {
    return { label: value, pending: false };
  }

  return { label: 'Awaiting Submission', pending: true };
};

const isWellbeingSetSubmitted = (scores: WellbeingScores | null) => {
  if (!scores) return false;
  return WELLBEING_METRICS.every(({ key }) => !isWellbeingValueMissing(scores[key]));
};

export const AssessmentSection: React.FC<AssessmentSectionProps> = ({
  clientId,
  currentProgramme,
  userRole,
  initialGoal,
}) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assessmentData, setAssessmentData] = useState<AssessmentData[]>([]);
  const [wellbeingData, setWellbeingData] = useState<{ initial: WellbeingScores | null; review: WellbeingScores | null }>({
    initial: null,
    review: null,
  });
  const [preAssessmentStatus, setPreAssessmentStatus] = useState({
    preHealthConsult: false,
    healthConsult: false,
    intakeForm: false,
  });
  const [showFoundation, setShowFoundation] = useState(false);
  const [showIgnition, setShowIgnition] = useState(false);
  const [expandedProgramme, setExpandedProgramme] = useState<'Foundation' | 'Ignition' | null>(null);

  // Dialog state
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<PreAssessmentType | null>(null);
  const [showAlliedHealthDialog, setShowAlliedHealthDialog] = useState(false);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);
  const [pendingOverride, setPendingOverride] = useState<{ stage: 'Initial' | 'Review' } | null>(null);

  // Report generation states
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportGenerationProgress, setReportGenerationProgress] = useState(0);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [generatedReportUrl, setGeneratedReportUrl] = useState('');
  const [reportGenerationError, setReportGenerationError] = useState<string | null>(null);
  const [reportTypeBeingGenerated, setReportTypeBeingGenerated] = useState<'Initial' | 'Review' | null>(null);
  const [isWellbeingExpanded, setIsWellbeingExpanded] = useState(true);

  // Programme selection modal states
  const [showProgrammeSelectionModal, setShowProgrammeSelectionModal] = useState(false);
  const [pendingReportStage, setPendingReportStage] = useState<'Initial' | 'Review' | null>(null);

  // Track if initial auto-expand has occurred
  const hasAutoExpandedRef = useRef(false);

  // Detect which programmes to show
  const detectProgrammes = useCallback(() => {
    const programmeLower = currentProgramme?.toLowerCase() || '';

    // Check current programme
    const hasFoundationInCurrent = programmeLower.includes('foundation');
    const hasIgnitionInCurrent = programmeLower.includes('ignition');

    // Check assessment data
    const hasFoundationData = assessmentData.some(d => d.programmeType === 'Foundation');
    const hasIgnitionData = assessmentData.some(d => d.programmeType === 'Ignition');

    setShowFoundation(hasFoundationInCurrent || hasFoundationData);
    setShowIgnition(hasIgnitionInCurrent || hasIgnitionData);

    // Auto-expand the current programme ONLY on initial load
    if (!hasAutoExpandedRef.current) {
      if (hasFoundationInCurrent) {
        setExpandedProgramme('Foundation');
        hasAutoExpandedRef.current = true;
      } else if (hasIgnitionInCurrent) {
        setExpandedProgramme('Ignition');
        hasAutoExpandedRef.current = true;
      }
    }
  }, [currentProgramme, assessmentData]);

  // Fetch assessment data
  const fetchAssessmentData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch client document for pre-assessment status
      const clientDoc = await getDoc(doc(db, 'bigquery_cache/client_data/comprehensive_details', clientId));
      const clientData = clientDoc.data();
      
      let reviewWellbeing: WellbeingScores | null = null;
      if (clientData) {
        setPreAssessmentStatus({
          preHealthConsult: clientData.pre_health_consult_data_submitted || false,
          healthConsult: clientData.health_consult_data_submitted || false,
          intakeForm: clientData.client_intake_data_submitted || false,
        });

        reviewWellbeing = {
          healthRating: clientData.review_wellbeing_health_rating ?? null,
          moderateExerciseMinutes: clientData.review_wellbeing_moderate_exercise_minutes ?? null,
          resistanceTrainingSessions: clientData.review_wellbeing_resistance_training_days ?? null,
          vigorousExerciseMinutes: clientData.review_wellbeing_vigorous_exercise_minutes ?? null,
          wellbeingIndexScore: clientData.review_wellbeing_wellbeing_index_score ?? null,
        };
      }

      let initialWellbeing: WellbeingScores | null = null;
      try {
        const intakeDoc = await getDoc(doc(db, 'client_intake', clientId));
        if (intakeDoc.exists()) {
          const intakeData = intakeDoc.data();
          const { responses, score } = parseWellbeingIndex(intakeData.wellbeing_index);

          initialWellbeing = {
            healthRating: intakeData.health_rating ?? null,
            moderateExerciseMinutes: intakeData.exercise_volume ?? null,
            resistanceTrainingSessions: intakeData.resistance_training ?? null,
            vigorousExerciseMinutes: intakeData.vigorous_exercise_volume ?? null,
            wellbeingIndexScore: score,
            wellbeingIndexResponses: responses,
            rawWellbeingIndex: typeof intakeData.wellbeing_index === 'string' ? intakeData.wellbeing_index : undefined,
          };
        }
      } catch (wellbeingError) {
        console.error('Error fetching initial wellbeing data:', wellbeingError);
      }

      setWellbeingData({
        initial: initialWellbeing,
        review: reviewWellbeing,
      });

      // Fetch assessment data from report_submissions structure
      const assessments: AssessmentData[] = [];
      
      // Try to fetch Initial assessments as subcollection first
      try {
        const initialCollectionRef = collection(db, 'report_submissions', clientId, 'Initial');
        const initialSnapshot = await getDocs(initialCollectionRef);
        
        initialSnapshot.forEach((doc) => {
          const data = doc.data();
          // Extract uploadCategory from various possible locations
          const uploadCategory = data.uploadCategory || 
                               data.formData?.uploadCategory || 
                               data.formData?.['Upload category (initial)'] || 
                               data.formData?.['Upload category'] ||
                               '';
          
          assessments.push({
            programmeType: data.formData?.['Programme Type'] || 'Ignition',
            assessmentStage: 'Initial',
            formData: data.formData || {},
            timestamp: data.timestamp?.toDate() || new Date(),
            uploadCategory: uploadCategory,
            responseId: data.responseId || data.formData?.responseId,
          });
        });
      } catch (error) {
        console.log('Error fetching Initial subcollection, trying document approach:', error);
        // If subcollection doesn't exist, try as document
        try {
          const initialDocRef = doc(db, 'report_submissions', clientId, 'Initial');
          const initialDoc = await getDoc(initialDocRef);
          
          if (initialDoc.exists()) {
            const data = initialDoc.data();
            const uploadCategory = data.uploadCategory || 
                                 data.formData?.uploadCategory || 
                                 data.formData?.['Upload category (initial)'] || 
                                 data.formData?.['Upload category'] ||
                                 '';
            
            assessments.push({
              programmeType: data.formData?.['Programme Type'] || 'Ignition',
              assessmentStage: 'Initial',
              formData: data.formData || {},
              timestamp: data.timestamp?.toDate() || new Date(),
              uploadCategory: uploadCategory,
              responseId: data.responseId || data.formData?.responseId,
            });
          }
        } catch (docError) {
          console.log('Error fetching Initial document:', docError);
        }
      }

      // Try to fetch Review assessments as subcollection first
      try {
        const reviewCollectionRef = collection(db, 'report_submissions', clientId, 'Review');
        const reviewSnapshot = await getDocs(reviewCollectionRef);
        
        reviewSnapshot.forEach((doc) => {
          const data = doc.data();
          // Extract uploadCategory from various possible locations
          const uploadCategory = data.uploadCategory || 
                               data.formData?.uploadCategory || 
                               data.formData?.['Upload category (initial)'] || 
                               data.formData?.['Upload category'] ||
                               '';
          
          assessments.push({
            programmeType: data.formData?.['Programme Type'] || 'Ignition',
            assessmentStage: 'Review',
            formData: data.formData || {},
            timestamp: data.timestamp?.toDate() || new Date(),
            uploadCategory: uploadCategory,
            responseId: data.responseId || data.formData?.responseId,
          });
        });
      } catch (error) {
        console.log('Error fetching Review subcollection, trying document approach:', error);
        // If subcollection doesn't exist, try as document
        try {
          const reviewDocRef = doc(db, 'report_submissions', clientId, 'Review');
          const reviewDoc = await getDoc(reviewDocRef);
          
          if (reviewDoc.exists()) {
            const data = reviewDoc.data();
            const uploadCategory = data.uploadCategory || 
                                 data.formData?.uploadCategory || 
                                 data.formData?.['Upload category (initial)'] || 
                                 data.formData?.['Upload category'] ||
                                 '';
            
            assessments.push({
              programmeType: data.formData?.['Programme Type'] || 'Ignition',
              assessmentStage: 'Review',
              formData: data.formData || {},
              timestamp: data.timestamp?.toDate() || new Date(),
              uploadCategory: uploadCategory,
              responseId: data.responseId || data.formData?.responseId,
            });
          }
        } catch (docError) {
          console.log('Error fetching Review document:', docError);
        }
      }

      console.log('Fetched assessments:', assessments);
      setAssessmentData(assessments);
    } catch (err) {
      console.error('Error fetching assessment data:', err);
      setError('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Setup real-time listeners
  useEffect(() => {
    fetchAssessmentData();
    
    // Set up real-time listeners for assessment updates
    const unsubscribers: (() => void)[] = [];
    
    try {
      // Try to set up listener for Initial subcollection
      const unsubscribeInitial = onSnapshot(
        collection(db, 'report_submissions', clientId, 'Initial'),
        () => fetchAssessmentData(),
        (error) => console.log('Initial collection listener error:', error)
      );
      unsubscribers.push(unsubscribeInitial);
    } catch (error) {
      console.log('Could not set up Initial collection listener:', error);
    }
    
    try {
      // Try to set up listener for Review subcollection
      const unsubscribeReview = onSnapshot(
        collection(db, 'report_submissions', clientId, 'Review'),
        () => fetchAssessmentData(),
        (error) => console.log('Review collection listener error:', error)
      );
      unsubscribers.push(unsubscribeReview);
    } catch (error) {
      console.log('Could not set up Review collection listener:', error);
    }

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [clientId, fetchAssessmentData]);

  // Detect programmes when data changes
  useEffect(() => {
    detectProgrammes();
  }, [detectProgrammes]);

  // Calculate statistics
  const statistics = useMemo(() => {
    let totalAssessments = 0;
    let completedAssessments = 0;

    // Count pre-assessments
    totalAssessments += 3;
    completedAssessments += Object.values(preAssessmentStatus).filter(Boolean).length;

    // Count programme assessments
    if (showFoundation) {
      totalAssessments += 9; // 5 initial + 4 review (no Allied Health in review)
      completedAssessments += assessmentData.filter(d => d.programmeType === 'Foundation').length;
    }
    
    if (showIgnition) {
      totalAssessments += 10; // 5 initial + 5 review
      completedAssessments += assessmentData.filter(d => d.programmeType === 'Ignition').length;
    }

    return { total: totalAssessments, completed: completedAssessments };
  }, [preAssessmentStatus, assessmentData, showFoundation, showIgnition]);

  // Check if can generate reports
  const canGenerateReports = useMemo(() => {
    const checkAllSubmitted = (programmeType: 'Foundation' | 'Ignition', stage: 'Initial' | 'Review') => {
      const categories = programmeType === 'Foundation' 
        ? stage === 'Initial' 
          ? ['Inbody', 'Movement', 'Allied Health', 'Nutrition', 'Training']
          : ['Inbody', 'Movement', 'Nutrition', 'Training'] // No Allied Health for Foundation Review
        : ['Inbody', 'Movement', 'Metabolic', 'Nutrition', 'Training'];
      
      return categories.every(cat => 
        assessmentData.some(d => 
          d.programmeType === programmeType && 
          d.assessmentStage === stage && 
          d.uploadCategory.toLowerCase().includes(cat.toLowerCase())
        )
      );
    };

    const foundationInitial = showFoundation && checkAllSubmitted('Foundation', 'Initial');
    const foundationReview = showFoundation && checkAllSubmitted('Foundation', 'Review');
    const ignitionInitial = showIgnition && checkAllSubmitted('Ignition', 'Initial');
    const ignitionReview = showIgnition && checkAllSubmitted('Ignition', 'Review');

    return {
      initial: foundationInitial || ignitionInitial,
      review: foundationReview || ignitionReview,
      foundation: {
        initial: foundationInitial,
        review: foundationReview,
      },
      ignition: {
        initial: ignitionInitial,
        review: ignitionReview,
      },
    };
  }, [assessmentData, showFoundation, showIgnition]);

  const handlePreAssessmentClick = (type: PreAssessmentType) => {
    setSelectedAssessmentType(type);
    setShowAssessmentDialog(true);
  };

  const handleProgrammeAssessmentClick = useCallback((category: AssessmentCategory, stage: 'Initial' | 'Review') => {
    if (category === 'Allied Health' && stage === 'Initial') {
      setShowAlliedHealthDialog(true);
    }
  }, []);
  
  // Helper function to determine programme for override
  const determineProgrammeForOverride = (stage: 'Initial' | 'Review'): 'Foundation' | 'Ignition' | 'SHOW_MODAL' => {
    // If both programmes are shown, let user choose
    if (showFoundation && showIgnition) {
      return 'SHOW_MODAL';
    }

    // If only one programme is shown, use it
    if (showFoundation && !showIgnition) return 'Foundation';
    if (showIgnition && !showFoundation) return 'Ignition';

    // Neither shown - determine from currentProgramme
    const programmeLower = currentProgramme?.toLowerCase() || '';
    if (programmeLower.includes('foundation')) return 'Foundation';
    if (programmeLower.includes('ignition')) return 'Ignition';

    // Default fallback
    return 'Ignition';
  };

  const handleReportButtonClick = (stage: 'Initial' | 'Review', isOverride?: boolean) => {
    // If override mode, show confirmation dialog first
    if (isOverride) {
      setPendingOverride({ stage });
      setShowOverrideConfirm(true);
      return;
    }

    // Normal flow (existing logic)
    // Check if both programmes have completed assessments for this stage
    const bothProgrammesReady = canGenerateReports.foundation[stage.toLowerCase() as 'initial' | 'review'] &&
                               canGenerateReports.ignition[stage.toLowerCase() as 'initial' | 'review'];

    if (bothProgrammesReady) {
      // Show programme selection modal
      setPendingReportStage(stage);
      setShowProgrammeSelectionModal(true);
    } else {
      // Only one programme is ready, proceed directly
      handleGenerateReport(stage);
    }
  };

  const handleOverrideConfirm = () => {
    setShowOverrideConfirm(false);
    if (!pendingOverride) return;

    const { stage } = pendingOverride;
    const programme = determineProgrammeForOverride(stage);

    if (programme === 'SHOW_MODAL') {
      setPendingReportStage(stage);
      setShowProgrammeSelectionModal(true);
    } else {
      handleGenerateReport(stage, programme);
    }

    setPendingOverride(null);
  };

  const handleOverrideCancel = () => {
    setShowOverrideConfirm(false);
    setPendingOverride(null);
  };
  
  const handleProgrammeSelection = (programme: 'Foundation' | 'Ignition') => {
    setShowProgrammeSelectionModal(false);
    if (pendingReportStage) {
      handleGenerateReport(pendingReportStage, programme);
    }
    setPendingReportStage(null);
  };

  const mapPreAssessmentType = (type: PreAssessmentType | null): 'pre_health_consult' | 'health_consult' | 'client_intake' | null => {
    if (!type) return null;
    switch (type) {
      case 'Pre-Health Consult':
        return 'pre_health_consult';
      case 'Health Consult':
        return 'health_consult';
      case 'Intake Form':
        return 'client_intake';
      default:
        return null;
    }
  };

  const handleGenerateReport = async (stage: 'Initial' | 'Review', programme?: 'Foundation' | 'Ignition') => {
    if (!clientId) {
      enqueueSnackbar('Client ID is required', { variant: 'error' });
      return;
    }
    
    if (!user || !user.uid) {
      enqueueSnackbar('You must be logged in to generate reports', { variant: 'error' });
      return;
    }
    
    // Determine the report type
    let reportType: string = stage;
    if (!programme) {
      // If no programme specified, determine which one is available
      if (canGenerateReports.foundation[stage.toLowerCase() as 'initial' | 'review']) {
        reportType = `Foundation_${stage}`;
      } else if (canGenerateReports.ignition[stage.toLowerCase() as 'initial' | 'review']) {
        reportType = stage; // Ignition uses the original format
      }
    } else {
      // Programme is specified
      reportType = programme === 'Foundation' ? `Foundation_${stage}` : stage;
    }
    
    setIsGeneratingReport(true);
    setReportGenerationProgress(10);
    setShowReportDialog(true);
    setReportTypeBeingGenerated(stage);
    setReportGenerationError(null);
    setGeneratedReportUrl('');
    
    // Start the progress animation with dynamic speed
    let progressStartTime = Date.now();
    const progressInterval = setInterval(() => {
      setReportGenerationProgress(prev => {
        const elapsed = (Date.now() - progressStartTime) / 1000; // seconds elapsed
        
        // Dynamic progress based on elapsed time
        if (elapsed < 30) {
          // First 30 seconds: reach 50%
          return Math.min(50, prev + 2);
        } else if (elapsed < 60) {
          // 30-60 seconds: reach 70%
          return Math.min(70, prev + 1);
        } else if (elapsed < 120) {
          // 60-120 seconds: reach 85%
          return Math.min(85, prev + 0.5);
        } else {
          // After 2 minutes: slow progress to 88%
          return Math.min(88, prev + 0.1);
        }
      });
    }, 1000); // Update every second
    
    try {
      // Check if user document exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      // If user document doesn't exist, create it
      if (!userDoc.exists()) {
        console.log('Creating user document for logged in user');
        try {
          await setDoc(userDocRef, {
            email: user.email || '',
            displayName: user.displayName || '',
            photoURL: user.photoURL || '',
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
            role: 'user' // Default role
          });
          console.log('User document created successfully');
        } catch (createUserError) {
          console.error('Error creating user document:', createUserError);
          // Continue with report generation even if user document creation fails
        }
      }
      
      // Make POST request to generate report
      console.log('Sending report generation request with data:', {
        client_id: clientId,
        report_type: reportType,
        user_id: user.uid,
        user_email: user.email || '',
        user_name: user.displayName || ''
      });
      
      // Set up a timeout for the API call (4 minutes to ensure Cloud Function has time to complete)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 240000);
      
      // Use our Cloud Function instead of directly calling Google Apps Script
      const response = await fetch('https://us-central1-ufit-client-tracker.cloudfunctions.net/generateReport', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          report_type: reportType,
          user_id: user.uid,
          user_email: user.email || '',
          user_name: user.displayName || ''
        }),
        signal: controller.signal
      });
      
      // Clear the timeout since we got a response
      clearTimeout(timeoutId);
      
      // Set progress to 90% when we get a response
      setReportGenerationProgress(90);
      
      console.log('Report generation response status:', response.status);
      
      if (!response.ok) {
        let errorMessage = 'Failed to generate report';
        try {
          const errorText = await response.text();
          console.error('Error response text:', errorText);
          errorMessage = `Failed to generate report: ${errorText || response.statusText}`;
        } catch (textError) {
          console.error('Error reading response text:', textError);
        }
        throw new Error(errorMessage);
      }
      
      let result;
      try {
        const responseText = await response.text();
        console.log('Response text:', responseText);
        
        // Try to parse the response as JSON
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error('Error parsing JSON response:', parseError);
          throw new Error('Invalid JSON response from report generation service');
        }
      } catch (responseError) {
        console.error('Error reading response:', responseError);
        throw new Error('Failed to read response from report generation service');
      }
      
      console.log('Parsed response data:', result);
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      // Check for URL in different possible locations (legacy uses data.url)
      const reportUrl = result.reportUrl || result.url;
      
      if (!reportUrl) {
        console.error('No URL found in response:', result);
        throw new Error('No report URL received');
      }
      
      // Set progress to 95%
      setReportGenerationProgress(95);
      
      // Store the report URL
      setGeneratedReportUrl(reportUrl);
      
      // Save to Firestore (matching legacy implementation)
      try {
        const reportsRef = collection(db, 'clients', clientId, 'reports');
        await addDoc(reportsRef, {
          client_id: clientId,
          report_type: reportType,
          stage: stage,
          programme: reportType.startsWith('Foundation_') ? 'Foundation' : 'Ignition',
          url: reportUrl,
          download_url: `https://drive.google.com/uc?export=download&id=${extractFileIdFromUrl(reportUrl)}`,
          generated_at: new Date().toISOString(),
          report_status: 'Generated',
          created_at: new Date().toISOString(),
          generated_by: {
            uid: user.uid,
            email: user.email || '',
            name: user.displayName || ''
          },
          latest_report: {
            url: reportUrl,
            report_type: reportType,
            generated_by: user.displayName || user.email || 'Unknown'
          }
        });
        
        setReportGenerationProgress(100);
        enqueueSnackbar('Report generated successfully!', { variant: 'success' });
        
      } catch (firestoreError) {
        console.error('Error saving report to Firestore:', firestoreError);
        enqueueSnackbar('Report generated but failed to save. Please try again.', { variant: 'warning' });
        // Still show the report URL even if save fails
        setReportGenerationProgress(100);
      }
      
    } catch (error: any) {
      console.error('Error generating report:', error);
      let errorMessage = 'Failed to generate report';
      
      if (error.name === 'AbortError') {
        errorMessage = 'Report generation timed out. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setReportGenerationError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      clearInterval(progressInterval);
      setIsGeneratingReport(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const renderWellbeingTiles = (scores: WellbeingScores) => (
    <Grid container spacing={2}>
      {WELLBEING_METRICS.map(({ key, label }) => {
        const { label: displayValue, pending } = getWellbeingDisplay(scores[key]);
        return (
          <Grid item xs={12} sm={6} key={key}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                height: '100%',
                bgcolor: theme => alpha(theme.palette.warning.main, 0.08),
                borderColor: theme => alpha(theme.palette.warning.main, 0.24),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: theme => alpha(theme.palette.warning.main, 0.2),
                  color: theme => theme.palette.warning.dark,
                }}
              >
                {getWellbeingIcon(key)}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {label}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ mt: 0.25 }}
                  noWrap
                  color={pending ? 'warning.main' : 'text.primary'}
                >
                  {displayValue}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );

  const initialSubmitted = isWellbeingSetSubmitted(wellbeingData.initial);
  const reviewSubmitted = isWellbeingSetSubmitted(wellbeingData.review);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Assessment Status Header */}
        <motion.div variants={itemVariants}>
          <AssessmentStatusHeader
            totalAssessments={statistics.total}
            completedAssessments={statistics.completed}
            onGenerateReport={handleReportButtonClick}
            canGenerateInitial={canGenerateReports.initial}
            canGenerateReview={canGenerateReports.review}
            isGenerating={isGeneratingReport}
            userRole={userRole}
          />
        </motion.div>

        {/* Pre-Assessment Cards */}
        <motion.div variants={itemVariants}>
          <PreAssessmentCards
            clientId={clientId}
            preHealthConsultSubmitted={preAssessmentStatus.preHealthConsult}
            healthConsultSubmitted={preAssessmentStatus.healthConsult}
            intakeFormSubmitted={preAssessmentStatus.intakeForm}
            onAssessmentClick={handlePreAssessmentClick}
          />
        </motion.div>

        {/* Client Form Links Accordion */}
        <motion.div variants={itemVariants} style={{ marginBottom: '24px' }}>
          <Accordion
            elevation={!preAssessmentStatus.intakeForm ? 2 : 0}
            sx={{
              border: '2px solid',
              borderColor: !preAssessmentStatus.intakeForm
                ? theme => theme.palette.warning.main
                : 'divider',
              borderRadius: 2,
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: 0,
              },
              backgroundColor: !preAssessmentStatus.intakeForm
                ? theme => alpha(theme.palette.warning.main, 0.08)
                : 'transparent',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                borderRadius: 2,
                '&.Mui-expanded': {
                  minHeight: 48,
                },
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  gap: 1.5,
                  '&.Mui-expanded': {
                    margin: '12px 0',
                  }
                }
              }}
            >
              <LinkIcon sx={{
                color: !preAssessmentStatus.intakeForm ? 'warning.main' : 'primary.main',
                fontSize: 22
              }} />
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1rem',
                  fontWeight: !preAssessmentStatus.intakeForm ? 600 : 500,
                  color: !preAssessmentStatus.intakeForm ? 'warning.dark' : 'text.primary'
                }}
              >
                Client Form Links
              </Typography>
              {!preAssessmentStatus.intakeForm && (
                <Chip
                  label="Action Required"
                  size="small"
                  color="warning"
                  sx={{ ml: 1, fontWeight: 600 }}
                />
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Form Description */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Client Intake Form
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Link includes: Client ID and Final Agreed Goal
                  </Typography>
                  {!initialGoal && (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      Warning: Final Agreed Goal is not available
                    </Typography>
                  )}
                </Box>

                {/* URL Display */}
                <Box>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5 }}>
                    Intake Form URL:
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        fontSize: '0.85rem',
                        lineHeight: 1.6,
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                        flexGrow: 1,
                        width: '100%',
                        overflow: 'auto'
                      }}
                    >
                      {`https://docs.google.com/forms/d/e/1FAIpQLSeGM8yTe3Of2K_qdgxe09b6BiirKZxcMXtO8qFvMkaeJvADTA/viewform?usp=pp_url&entry.2053908834=${clientId}&entry.1365593976=${encodeURIComponent(initialGoal || '')}`}
                    </Box>
                    <Tooltip title="Open in new tab" placement="top">
                      <IconButton
                        size="medium"
                        onClick={() => {
                          const url = `https://docs.google.com/forms/d/e/1FAIpQLSeGM8yTe3Of2K_qdgxe09b6BiirKZxcMXtO8qFvMkaeJvADTA/viewform?usp=pp_url&entry.2053908834=${clientId}&entry.1365593976=${encodeURIComponent(initialGoal || '')}`;
                          window.open(url, '_blank');
                        }}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark'
                          }
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </motion.div>

        {/* Self Reported Wellbeing */}
        <motion.div variants={itemVariants}>
          <Accordion
            expanded={isWellbeingExpanded}
            onChange={(_, expanded) => setIsWellbeingExpanded(expanded)}
            disableGutters
            sx={{
              borderRadius: 2,
              border: theme => `1px solid ${alpha(theme.palette.warning.main, 0.24)}`,
              boxShadow: 'none',
              backgroundColor: theme => alpha(theme.palette.warning.main, 0.12),
              '&::before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2,
                py: 1.5,
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                  display: 'flex',
                  gap: 1.5,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexGrow: 1,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme => alpha(theme.palette.warning.main, 0.2),
                    color: theme => theme.palette.warning.dark,
                  }}
                >
                  <SentimentSatisfiedAltIcon fontSize="small" />
                </Box>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  Self Reported Wellbeing
                </Typography>
              </Box>
              {!isWellbeingExpanded && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    ml: 2,
                  }}
                >
                  <Chip
                    size="small"
                    variant="filled"
                    label={initialSubmitted ? 'Initial: Submitted' : 'Initial: Awaiting Submission'}
                    icon={initialSubmitted ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                    sx={getSubmissionChipSx('Initial', initialSubmitted)}
                  />
                  <Chip
                    size="small"
                    variant="filled"
                    label={reviewSubmitted ? 'Review: Submitted' : 'Review: Awaiting Submission'}
                    icon={reviewSubmitted ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                    sx={getSubmissionChipSx('Review', reviewSubmitted)}
                  />
                </Box>
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, py: 2 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Initial Scores
                  </Typography>
                  {renderWellbeingTiles(wellbeingData.initial || EMPTY_WELLBEING_SCORES)}
                  {!wellbeingData.initial && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      No initial wellbeing submission on file yet.
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: theme => alpha(theme.palette.warning.main, 0.24) }} />

                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Review Scores
                  </Typography>
                  {renderWellbeingTiles(wellbeingData.review || EMPTY_WELLBEING_SCORES)}
                  {!wellbeingData.review && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      No review wellbeing submission on file yet.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </motion.div>

        {/* Programme Sections */}
        <Stack spacing={2} sx={{ mt: 3 }}>
          <AnimatePresence>
            {showFoundation && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <ProgrammeAssessmentSection
                  programmeType="Foundation"
                  clientId={clientId}
                  currentProgramme={currentProgramme}
                  assessmentData={assessmentData}
                  onAssessmentClick={handleProgrammeAssessmentClick}
                  isExpanded={expandedProgramme === 'Foundation'}
                  onToggleExpand={() => setExpandedProgramme(
                    expandedProgramme === 'Foundation' ? null : 'Foundation'
                  )}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showIgnition && (
              <motion.div
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <ProgrammeAssessmentSection
                  programmeType="Ignition"
                  clientId={clientId}
                  currentProgramme={currentProgramme}
                  assessmentData={assessmentData}
                  onAssessmentClick={handleProgrammeAssessmentClick}
                  isExpanded={expandedProgramme === 'Ignition'}
                  onToggleExpand={() => setExpandedProgramme(
                    expandedProgramme === 'Ignition' ? null : 'Ignition'
                  )}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Stack>

        {/* No Programme Message */}
        {!showFoundation && !showIgnition && (
          <motion.div variants={itemVariants}>
            <Fade in timeout={500}>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  No Programme Data Available
                </Typography>
                <Typography variant="body2">
                  Assessment data will appear here once the client is enrolled in a Foundation or Ignition programme.
                </Typography>
              </Alert>
            </Fade>
          </motion.div>
        )}
      </motion.div>

      {/* Assessment Dialog */}
      {selectedAssessmentType && (
        <ClientAssessmentViewer
          open={showAssessmentDialog}
          onClose={() => {
            setShowAssessmentDialog(false);
            setSelectedAssessmentType(null);
          }}
          clientId={clientId}
          assessmentType={mapPreAssessmentType(selectedAssessmentType)!}
        />
      )}

      <AlliedHealthAssessmentDialog
        open={showAlliedHealthDialog}
        onClose={() => setShowAlliedHealthDialog(false)}
        clientId={clientId}
      />

      {/* Report Generation Modal */}
      <ReportGenerationModal
        open={showReportDialog}
        onClose={() => {
          if (!isGeneratingReport) {
            setShowReportDialog(false);
            setReportGenerationProgress(0);
            setGeneratedReportUrl('');
            setReportGenerationError(null);
            setReportTypeBeingGenerated(null);
          }
        }}
        reportType={reportTypeBeingGenerated || 'Initial'}
        isGenerating={isGeneratingReport}
        progress={reportGenerationProgress}
        reportUrl={generatedReportUrl}
        error={reportGenerationError}
        onRetry={() => {
          if (reportTypeBeingGenerated) {
            handleReportButtonClick(reportTypeBeingGenerated);
          }
        }}
      />
      
      {/* Programme Selection Modal */}
      <ProgrammeSelectionModal
        open={showProgrammeSelectionModal}
        onClose={() => {
          setShowProgrammeSelectionModal(false);
          setPendingReportStage(null);
        }}
        onSelect={handleProgrammeSelection}
        reportType={pendingReportStage || 'Initial'}
        availableProgrammes={{
          foundation: canGenerateReports.foundation[pendingReportStage?.toLowerCase() as 'initial' | 'review'] || false,
          ignition: canGenerateReports.ignition[pendingReportStage?.toLowerCase() as 'initial' | 'review'] || false,
        }}
      />

      {/* Override Confirmation Dialog */}
      <Dialog
        open={showOverrideConfirm}
        onClose={handleOverrideCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Incomplete Assessment Data
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Not all required assessments have been submitted for the{' '}
            <strong>{pendingOverride?.stage}</strong> stage. Generating a report now
            will produce an <strong>incomplete report</strong> with missing data.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOverrideCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleOverrideConfirm}
            variant="contained"
            color="warning"
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
